import moment from "moment";
import { Reservation, ReservationStatus, Shift, Table } from "../model/reservation";
import { ReservationTag, ReservationTagCategory } from "../model/reservation-tag";
import { CategoryTagDTO } from "../model/tags.dto";
import { DayPart } from "./reservation.service";
import { CountryCode, ReservationType, Section } from "@model/tables-plan";

export class Common {

    static readonly WORKING_DAY_START_HOUR = 6;

    // this is an exhaustive list, use the getFilteredReservationStatuses() method of reservation.service
    // to get only the supported statuses for the selected restaurant
    static reservationStatuses: { id: ReservationStatus, img: string }[] = [
        { id: 'NotConfirmed', img: 'assets/images/status_notConfirmed.png' },
        { id: 'Confirmed', img: 'assets/images/status_confirmed.png' },
        { id: 'CheckedIn', img: 'assets/images/status_checkedIn.png' },
        { id: 'CheckedIn(Partial)', img: 'assets/images/status_checkedInPartial.png' },
        { id: 'LateBooking', img: 'assets/images/status_lateBooking.png' },
        { id: 'WalkIn', img: 'assets/images/status_walkIn.png' },
        { id: 'Pending', img: 'assets/images/status_pending.png' },
        { id: 'Waiting', img: 'assets/images/status_waiting.png' },
        { id: 'Prepare', img: 'assets/images/status_prepare.png' },
        { id: 'Dessert', img: 'assets/images/status_dessert.png' },
        { id: 'Payment', img: 'assets/images/status_payment.png' },
        { id: 'NotShown', img: 'assets/images/status_noShow.png' },
        { id: 'Cancelled', img: 'assets/images/status_cancelled.png' },
        { id: 'CheckedOut', img: 'assets/images/status_checkOut.png' },
        { id: 'Rejected', img: 'assets/images/status_rejected.png' },
    ];

    static optionalStatusIds = ['Dessert', 'Payment', 'Prepare'];

    static countryCodeList: CountryCode[] = [
        { code: "+30", country: "Greece" },
        { code: "+1", country: "Canada/USA" },
        { code: "+7", country: "Russia/Kazakhstan" },
        { code: "+20", country: "Egypt" },
        { code: "+27", country: "South Africa" },
        { code: "+31", country: "Netherlands" },
        { code: "+32", country: "Belgium" },
        { code: "+33", country: "France" },
        { code: "+34", country: "Spain" },
        { code: "+36", country: "Hungary" },
        { code: "+39", country: "Italy" },
        { code: "+40", country: "Romania" },
        { code: "+41", country: "Switzerland" },
        { code: "+43", country: "Austria" },
        { code: "+44", country: "United Kingdom" },
        { code: "+45", country: "Denmark" },
        { code: "+46", country: "Sweden" },
        { code: "+47", country: "Norway/Svalbard and Jan Mayen" },
        { code: "+48", country: "Poland" },
        { code: "+49", country: "Germany" },
        { code: "+51", country: "Peru" },
        { code: "+52", country: "Mexico" },
        { code: "+53", country: "Cuba" },
        { code: "+54", country: "Argentina" },
        { code: "+55", country: "Brazil" },
        { code: "+56", country: "Chile" },
        { code: "+57", country: "Colombia" },
        { code: "+58", country: "Venezuela" },
        { code: "+60", country: "Malaysia" },
        { code: "+61", country: "Australia/Christmas Island/Cocos Islands" },
        { code: "+62", country: "Indonesia" },
        { code: "+63", country: "Philippines" },
        { code: "+64", country: "New Zealand/Pitcairn" },
        { code: "+65", country: "Singapore" },
        { code: "+66", country: "Thailand" },
        { code: "+81", country: "Japan" },
        { code: "+82", country: "South Korea" },
        { code: "+84", country: "Vietnam" },
        { code: "+86", country: "China" },
        { code: "+90", country: "Turkey" },
        { code: "+91", country: "India" },
        { code: "+92", country: "Pakistan" },
        { code: "+93", country: "Afghanistan" },
        { code: "+94", country: "Sri Lanka" },
        { code: "+95", country: "Myanmar" },
        { code: "+98", country: "Iran" },
        { code: "+211", country: "South Sudan" },
        { code: "+212", country: "Morocco/Western Sahara" },
        { code: "+213", country: "Algeria" },
        { code: "+216", country: "Tunisia" },
        { code: "+218", country: "Libya" },
        { code: "+220", country: "Gambia" },
        { code: "+221", country: "Senegal" },
        { code: "+222", country: "Mauritania" },
        { code: "+223", country: "Mali" },
        { code: "+224", country: "Guinea" },
        { code: "+225", country: "Ivory Coast" },
        { code: "+226", country: "Burkina Faso" },
        { code: "+227", country: "Niger" },
        { code: "+228", country: "Togo" },
        { code: "+229", country: "Benin" },
        { code: "+230", country: "Mauritius" },
        { code: "+231", country: "Liberia" },
        { code: "+232", country: "Sierra Leone" },
        { code: "+233", country: "Ghana" },
        { code: "+234", country: "Nigeria" },
        { code: "+235", country: "Chad" },
        { code: "+236", country: "Central African Republic" },
        { code: "+237", country: "Cameroon" },
        { code: "+238", country: "Cape Verde" },
        { code: "+239", country: "Sao Tome and Principe" },
        { code: "+240", country: "Equatorial Guinea" },
        { code: "+241", country: "Gabon" },
        { code: "+242", country: "Republic of the Congo" },
        { code: "+243", country: "Democratic Republic of the Congo" },
        { code: "+244", country: "Angola" },
        { code: "+245", country: "Guinea-Bissau" },
        { code: "+246", country: "British Indian Ocean Territory" },
        { code: "+248", country: "Seychelles" },
        { code: "+249", country: "Sudan" },
        { code: "+250", country: "Rwanda" },
        { code: "+251", country: "Ethiopia" },
        { code: "+252", country: "Somalia" },
        { code: "+253", country: "Djibouti" },
        { code: "+254", country: "Kenya" },
        { code: "+255", country: "Tanzania" },
        { code: "+256", country: "Uganda" },
        { code: "+257", country: "Burundi" },
        { code: "+258", country: "Mozambique" },
        { code: "+260", country: "Zambia" },
        { code: "+261", country: "Madagascar" },
        { code: "+262", country: "Mayotte" },
        { code: "+262", country: "Reunion" },
        { code: "+263", country: "Zimbabwe" },
        { code: "+264", country: "Namibia" },
        { code: "+265", country: "Malawi" },
        { code: "+266", country: "Lesotho" },
        { code: "+267", country: "Botswana" },
        { code: "+268", country: "Swaziland" },
        { code: "+269", country: "Comoros" },
        { code: "+290", country: "Saint Helena" },
        { code: "+291", country: "Eritrea" },
        { code: "+297", country: "Aruba" },
        { code: "+298", country: "Faroe Islands" },
        { code: "+299", country: "Greenland" },
        { code: "+350", country: "Gibraltar" },
        { code: "+351", country: "Portugal" },
        { code: "+352", country: "Luxembourg" },
        { code: "+353", country: "Ireland" },
        { code: "+354", country: "Iceland" },
        { code: "+355", country: "Albania" },
        { code: "+356", country: "Malta" },
        { code: "+357", country: "Cyprus" },
        { code: "+358", country: "Finland" },
        { code: "+359", country: "Bulgaria" },
        { code: "+370", country: "Lithuania" },
        { code: "+371", country: "Latvia" },
        { code: "+372", country: "Estonia" },
        { code: "+373", country: "Moldova" },
        { code: "+374", country: "Armenia" },
        { code: "+375", country: "Belarus" },
        { code: "+376", country: "Andorra" },
        { code: "+377", country: "Monaco" },
        { code: "+378", country: "San Marino" },
        { code: "+379", country: "Vatican" },
        { code: "+380", country: "Ukraine" },
        { code: "+381", country: "Serbia" },
        { code: "+382", country: "Montenegro" },
        { code: "+383", country: "Kosovo" },
        { code: "+385", country: "Croatia" },
        { code: "+386", country: "Slovenia" },
        { code: "+387", country: "Bosnia and Herzegovina" },
        { code: "+389", country: "Macedonia" },
        { code: "+420", country: "Czech Republic" },
        { code: "+421", country: "Slovakia" },
        { code: "+423", country: "Liechtenstein" },
        { code: "+500", country: "Falkland Islands" },
        { code: "+501", country: "Belize" },
        { code: "+502", country: "Guatemala" },
        { code: "+503", country: "El Salvador" },
        { code: "+504", country: "Honduras" },
        { code: "+505", country: "Nicaragua" },
        { code: "+506", country: "Costa Rica" },
        { code: "+507", country: "Panama" },
        { code: "+508", country: "Saint Pierre and Miquelon" },
        { code: "+509", country: "Haiti" },
        { code: "+590", country: "Saint Barthelemy" },
        { code: "+590", country: "Saint Martin" },
        { code: "+591", country: "Bolivia" },
        { code: "+592", country: "Guyana" },
        { code: "+593", country: "Ecuador" },
        { code: "+595", country: "Paraguay" },
        { code: "+597", country: "Suriname" },
        { code: "+598", country: "Uruguay" },
        { code: "+599", country: "Curacao" },
        { code: "+599", country: "Netherlands Antilles" },
        { code: "+670", country: "East Timor" },
        { code: "+672", country: "Antarctica" },
        { code: "+673", country: "Brunei" },
        { code: "+674", country: "Nauru" },
        { code: "+675", country: "Papua New Guinea" },
        { code: "+676", country: "Tonga" },
        { code: "+677", country: "Solomon Islands" },
        { code: "+678", country: "Vanuatu" },
        { code: "+679", country: "Fiji" },
        { code: "+680", country: "Palau" },
        { code: "+681", country: "Wallis and Futuna" },
        { code: "+682", country: "Cook Islands" },
        { code: "+683", country: "Niue" },
        { code: "+685", country: "Samoa" },
        { code: "+686", country: "Kiribati" },
        { code: "+687", country: "New Caledonia" },
        { code: "+688", country: "Tuvalu" },
        { code: "+689", country: "French Polynesia" },
        { code: "+690", country: "Tokelau" },
        { code: "+691", country: "Micronesia" },
        { code: "+692", country: "Marshall Islands" },
        { code: "+850", country: "North Korea" },
        { code: "+852", country: "Hong Kong" },
        { code: "+853", country: "Macau" },
        { code: "+855", country: "Cambodia" },
        { code: "+856", country: "Laos" },
        { code: "+880", country: "Bangladesh" },
        { code: "+886", country: "Taiwan" },
        { code: "+960", country: "Maldives" },
        { code: "+961", country: "Lebanon" },
        { code: "+962", country: "Jordan" },
        { code: "+963", country: "Syria" },
        { code: "+964", country: "Iraq" },
        { code: "+965", country: "Kuwait" },
        { code: "+966", country: "Saudi Arabia" },
        { code: "+967", country: "Yemen" },
        { code: "+968", country: "Oman" },
        { code: "+970", country: "Palestine" },
        { code: "+971", country: "United Arab Emirates" },
        { code: "+972", country: "Israel" },
        { code: "+973", country: "Bahrain" },
        { code: "+974", country: "Qatar" },
        { code: "+975", country: "Bhutan" },
        { code: "+976", country: "Mongolia" },
        { code: "+977", country: "Nepal" },
        { code: "+992", country: "Tajikistan" },
        { code: "+993", country: "Turkmenistan" },
        { code: "+994", country: "Azerbaijan" },
        { code: "+995", country: "Georgia" },
        { code: "+996", country: "Kyrgyzstan" },
        { code: "+998", country: "Uzbekistan" },
        { code: "+1242", country: "Bahamas" },
        { code: "+1246", country: "Barbados" },
        { code: "+1264", country: "Anguilla" },
        { code: "+1268", country: "Antigua and Barbuda" },
        { code: "+1284", country: "British Virgin Islands" },
        { code: "+1340", country: "U.S. Virgin Islands" },
        { code: "+1345", country: "Cayman Islands" },
        { code: "+1441", country: "Bermuda" },
        { code: "+1473", country: "Grenada" },
        { code: "+1649", country: "Turks and Caicos Islands" },
        { code: "+1664", country: "Montserrat" },
        { code: "+1670", country: "Northern Mariana Islands" },
        { code: "+1671", country: "Guam" },
        { code: "+1684", country: "American Samoa" },
        { code: "+1721", country: "Sint Maarten" },
        { code: "+1758", country: "Saint Lucia" },
        { code: "+1767", country: "Dominica" },
        { code: "+1784", country: "Saint Vincent and the Grenadines" },
        { code: "+1787", country: "Puerto Rico" },
        { code: "+1939", country: "Puerto Rico" },
        { code: "+1809", country: "Dominican Republic" },
        { code: "+1829", country: "Dominican Republic" },
        { code: "+1849", country: "Dominican Republic" },
        { code: "+1868", country: "Trinidad and Tobago" },
        { code: "+1869", country: "Saint Kitts and Nevis" },
        { code: "+1876", country: "Jamaica" },
        { code: "+441481", country: "Guernsey" },
        { code: "+441534", country: "Jersey" },
        { code: "+441624", country: "Isle of Man" },
    ];

    static isPhoneNumber(input: string) {
        const re = /^[\+]?0{0,2}[(]?[0-9]{1,3}[)]?[-\s\.]?[0-9]{1,3}[-\s\.]?[0-9]{0,6}$/im;

        return input != null &&
            re.test(input);
    }

    static isEmpty(test: string | null) {
        return test == null || test.trim().length <= 0;
    }

    static uniqueId(): string {
        return "idxxxxxxxxxxxxxx".replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0,
                v = c === "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    static generateUuidBase64Encoded() {
        return Common.stripBase64PaddingCharacters(btoa(Common.uniqueId()));
    }

    private static stripBase64PaddingCharacters(paddedBase64: string) {
        // Removes last one or two == characters that are padding
        return paddedBase64.replace(/\=/g, "").replace(/\+/g, "-").replace(/\//g, "_");
    }

    static generateUUID() {
        var d = new Date().getTime();//Timestamp
        var d2 = ((typeof performance !== 'undefined') && performance.now && (performance.now() * 1000)) || 0;//Time in microseconds since page-load or 0 if unsupported
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16;//random number between 0 and 16
            if (d > 0) {//Use timestamp until depleted
                r = (d + r) % 16 | 0;
                d = Math.floor(d / 16);
            } else {//Use microseconds since page-load if supported
                r = (d2 + r) % 16 | 0;
                d2 = Math.floor(d2 / 16);
            }
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
    }

    static round(num: number, decimals: number): number {
        const multiplier = Math.pow(10, decimals);
        return Math.round(num * multiplier) / multiplier;
    }

    static getReservationStatusIcon(status: ReservationStatus) {
        switch (status) {
            case 'Waiting':
                return 'status_waiting.png';
            case 'Pending':
                return 'status_pending.png';
            case 'NotConfirmed':
                return 'status_notConfirmed.png';
            case 'Confirmed':
                return 'status_confirmed.png';
            case 'LateBooking':
                return 'status_lateBooking.png';
            case 'CheckedIn(Partial)':
                return 'status_checkedInPartial.png';
            case 'CheckedIn':
                return 'status_checkedIn.png';
            case 'WalkIn':
                return 'status_walkIn.png';
            case 'Dessert':
                return 'status_dessert.png';
            case 'Payment':
                return 'status_payment.png';
            case 'Prepare':
                return 'status_prepare.png';
            case 'CheckedOut':
                return 'status_checkOut.png';
            case 'NotShown':
                return 'status_noShow.png';
            case 'Cancelled':
                return 'status_cancelled.png';
            case 'Rejected':
                return 'status_rejected.png';
            default:
                return 'status_notConfirmed.png';
        }
    }

    static getReservationClass(status: ReservationStatus) {
        switch (status) {
            case 'Waiting':
                return 'reservation-waiting';
            case 'Pending':
                return 'reservation-pending';
            case 'NotConfirmed':
                return 'reservation-not-confirmed';
            case 'Confirmed':
                return 'reservation-confirmed';
            case 'LateBooking':
                return 'reservation-late-booking';
            case 'CheckedIn(Partial)':
                return 'reservation-checkedin-partial';
            case 'CheckedIn':
                return 'reservation-checkedin';
            case 'WalkIn':
                return 'reservation-walkin';
            case 'Dessert':
                return 'reservation-dessert';
            case 'Payment':
                return 'reservation-payment';
            case 'Prepare':
                return 'reservation-prepare';
            case 'CheckedOut':
                return 'reservation-checkout';
            case 'NotShown':
                return 'reservation-no-show';
            case 'Cancelled':
                return 'reservation-cancel';
            case 'Rejected':
                return 'reservation-rejected';
            default:
                return 'reservation-not-confirmed';
        }
    }

    static removePunctuation(text: string) {

        if (text == null) {
            return null;
        }

        return text
            .replace(/ά/g, 'α')
            .replace(/Ά/g, 'Α')
            .replace(/έ/g, 'ε')
            .replace(/Έ/g, 'Ε')
            .replace(/ή/g, 'η')
            .replace(/Ή/g, 'Η')
            .replace(/ί/g, 'ι')
            .replace(/Ί/g, 'Ι')
            .replace(/ό/g, 'ο')
            .replace(/Ό/g, 'Ο')
            .replace(/ύ/g, 'υ')
            .replace(/Ύ/g, 'Υ')
            .replace(/ώ/g, 'ω')
            .replace(/Ώ/g, 'Ω');
    }

    static timeBelongsToShift(time: Date | string, shift: Shift | null | undefined): boolean {

        if (shift == null) {
            return false;
        }

        if (shift.isFullDay) {
            return true;
        }

        const arrivalTime = moment(time);

        let fromTime = moment(time).set({ hour: shift.fromHour, minute: shift.fromMin });
        let toTime = moment(time).set({ hour: shift.toHour, minute: shift.toMin });

        if (toTime.isBefore(fromTime)) {
            if (arrivalTime.hour() < this.WORKING_DAY_START_HOUR) {
                fromTime.add(-1, 'days');
            } else {
                toTime.add(1, 'days');
            }
        }

        return arrivalTime.isSameOrAfter(fromTime) && arrivalTime.isSameOrBefore(toTime);
    }

    static getNearestDayPart(date?: Date, interval: 15 | 30 | 60 = 15): DayPart {

        const dayPart: DayPart = {};

        date = date ?? new Date();
        dayPart.hours = date.getHours();
        dayPart.minsFrom = Math.round(date.getMinutes() / interval) * interval;

        if (dayPart.minsFrom >= 60) {
            dayPart.minsFrom -= 60;
            dayPart.hours += 1;
        }

        return dayPart;
    }

    static isNullOrZero(value: number | string | undefined): boolean {

        if (value == null) {
            return true;
        }

        if (typeof value === 'number') {
            return value <= 0;
        }

        const num = parseFloat(value);

        return isNaN(num) || num <= 0;
    }

    static isNullOrEmpty(value: string | undefined | null): boolean {
        return value == null || value.trim().length <= 0;
    }

    static capitalizeFirstLetter(word: string | null | undefined) {
        if (word == null) {
            return null;
        }
        word = word.toLowerCase();
        return word.charAt(0).toUpperCase() + word.slice(1);
    }

    static initialSortReservations(reservations: Reservation[]): Reservation[] {
        return reservations.sort((a, b) => {

            const aId = a?.reservationDetails?.reservationId ?? '';
            const bId = b?.reservationDetails?.reservationId ?? '';

            if (aId > bId) {
                return -1;
            }

            if (aId < bId) {
                return 1;
            }

            return 0;
        });
    }

    static reservationCategoryDTOtoModel(dto: CategoryTagDTO): ReservationTagCategory {
        return new ReservationTagCategory({
            color: dto.color,
            name: dto.title,
            id: dto.id,
            order: dto.categoryOrder,
            type: dto.categoryType,
            tags: dto.tags?.sort((a, b) => a.tagOrder - b.tagOrder)?.map(t => ({
                id: t.id,
                title: t.title,
                categoryId: dto.id,
                tagOrder: t.tagOrder,
                deleted: false
            })) ?? []
        });
    }

    static reservationCategoryToDTO(res: ReservationTagCategory, order: number, restaurntId: string): CategoryTagDTO {
        return {
            id: "",
            categoryOrder: order,
            categoryType: 'RESERVATION',
            tags: [],
            restaurantId: restaurntId,
            color: res.color,
            title: res.name,
        }
    }

    static getStatusIndex(status: ReservationStatus | undefined) {
        if (status == null) {
            return 99999;
        }

        const indexes = {
            "Waiting": 0,
            "Pending": 1,
            "NotConfirmed": 2,
            "Confirmed": 3,
            "LateBooking": 4,
            "CheckedIn(Partial)": 5,
            "CheckedIn": 6,
            "WalkIn": 7,
            "Dessert": 8,
            "Payment": 9,
            "Prepare": 10,
            "CheckedOut": 11,
            "NotShown": 12,
            "Cancelled": 13,
            "Rejected": 14
        }

        return indexes[status];
    }

    static getCustomerTypeColor(customerTypeValue: string) {
        const customerTypeColors: { [id: string]: string; } = {
            'S': '#c9ba9c',
            'R': '#9e8cab',
            'VIP': '#c5958a',
            'R&VIP': '#cab145',
            'SVIP': '#859998',
            'BL': '#1a1a1a'
        };

        return customerTypeColors[customerTypeValue];
    }

    static tagCategoryOrderComperator(catA: ReservationTagCategory, catB: ReservationTagCategory): -1 | 0 | 1 {
        if ((catA.order ?? 0) > (catB.order ?? 0)) {
            return 1;
        }

        if ((catA.order ?? 0) < (catB.order ?? 0)) {
            return -1;
        }

        // necessary because some categories have the same order
        if (catA.id > catB.id) {
            return 1;
        }

        if (catA.id < catB.id) {
            return -1;
        }

        return 0;
    }

    static tagOrderComperator(tagA: ReservationTag, tagB: ReservationTag): -1 | 0 | 1 {
        if ((tagA.tagOrder ?? 0) > (tagB.tagOrder ?? 0)) {
            return 1;
        }

        if ((tagA.tagOrder ?? 0) < (tagB.tagOrder ?? 0)) {
            return -1;
        }

        return 0;
    }

    static timeIsNextDayMorning(date: Date): boolean {
        return date.getHours() < Common.WORKING_DAY_START_HOUR;
    }

    static normalizeGreek(input: string | null | undefined) {
        if (input == null) {
            return null;
        }
        var cvtValues = [
            { from: ['Ά', 'ά'], to: 'Α' },
            { from: ['Έ', 'έ'], to: 'Ε' },
            { from: ['Ή', 'ή'], to: 'Η' },
            { from: ['Ί', 'ί'], to: 'Ι' },
            { from: ['Ό', 'ό'], to: 'Ο' },
            { from: ['Ύ', 'ύ'], to: 'Υ' },
            { from: ['Ώ', 'ώ'], to: 'Ω' }
        ];

        for (var i = 0; i < cvtValues.length; i++) {
            for (var x = 0; x < cvtValues[i].from.length; x++) {
                input = input.replace(new RegExp(cvtValues[i].from[x], 'g'), cvtValues[i].to);
            }
        }

        return input;
    }

    // asc: numbers, letters, empty
    // desc: empty, letters, number
    static compareTableNames(tableA: string, tableB: string, sortDesc: boolean): -1 | 0 | 1 {

        if (Common.isNullOrEmpty(tableA)) {
            return sortDesc ? -1 : 1;
        } else if (Common.isNullOrEmpty(tableB)) {
            return sortDesc ? 1 : -1;
        }

        const numericRegex = /^\d+$/;
        const isTableANumericOnly = numericRegex.test(tableA);
        const isTableBNumericOnly = numericRegex.test(tableB);

        if (isTableANumericOnly && !isTableBNumericOnly) {
            return sortDesc ? 1 : -1;
        } else if (!isTableANumericOnly && isTableBNumericOnly) {
            return sortDesc ? -1 : 1;
        }

        if (isTableANumericOnly) {
            const tableANum = parseInt(tableA);
            const tableBNum = parseInt(tableB);

            if (sortDesc) {
                return tableANum > tableBNum ? -1 : 1;
            } else {
                return tableANum > tableBNum ? 1 : -1;
            }
        }

        // both tables have letters and numbers
        const tableANumberStr = tableA.replace(/^\D+/g, '');
        const tableBNumberStr = tableB.replace(/^\D+/g, '');
        const tableANumber = parseInt(tableANumberStr);
        const tableBNumber = parseInt(tableBNumberStr);
        const tableALetterPart = tableA.replace(tableANumberStr, '').toLowerCase();
        const tableBLetterPart = tableB.replace(tableBNumberStr, '').toLowerCase();

        if (tableALetterPart !== tableBLetterPart) {
            if (sortDesc) {
                return tableALetterPart > tableBLetterPart ? -1 : 1;
            } else {
                return tableALetterPart > tableBLetterPart ? 1 : -1;
            }

        } else {
            if (sortDesc) {
                return tableANumber > tableBNumber ? -1 : 1;
            } else {
                return tableANumber > tableBNumber ? 1 : -1;
            }
        }

        return 0;
    }

    static arrivalTimeComparator(reservationA: Reservation, reservationB: Reservation): -1 | 0 | 1 {

        const timeA = moment(reservationA.reservationDetails.arrivalTime);
        const timeB = moment(reservationB.reservationDetails.arrivalTime);

        if (timeA.isValid() && timeB.isValid()) {
            if (timeA.isBefore(timeB)) {
                return -1;
            } else if (timeA.isAfter(timeB)) {
                return 1;
            } else if ((reservationA.reservationDetails.reservationId ?? '') < (reservationB.reservationDetails.reservationId ?? '')) {
                return -1;
            } else if ((reservationA.reservationDetails.reservationId ?? '') > (reservationB.reservationDetails.reservationId ?? '')) {
                return 1;
            } else {
                return 0;
            }
        } else if (timeA.isValid()) {
            return -1;
        } else if (timeB.isValid()) {
            return 1;
        } else {
            if ((reservationA.reservationDetails.reservationId ?? '') < (reservationB.reservationDetails.reservationId ?? '')) {
                return -1;
            } else if ((reservationA.reservationDetails.reservationId ?? '') > (reservationB.reservationDetails.reservationId ?? '')) {
                return 1;
            } else {
                return 0;
            }
        }
    }

    static tableOrderComperator(tableA: Table, tableB: Table): -1 | 0 | 1 {
        if (tableA == null || tableB == null) {
            return 0;
        }

        if (tableA.tableOrder < tableB.tableOrder) {
            return -1;
        }
        if (tableA.tableOrder > tableB.tableOrder) {
            return 1;
        }
        return 0;
    }

    static sectionOrderComperator(sectionA: Section, sectionB: Section): -1 | 0 | 1 {
        if (sectionA == null || sectionB == null) {
            return 0;
        }

        if (sectionA.sectionOrder < sectionB.sectionOrder) {
            return -1;
        }
        if (sectionA.sectionOrder > sectionB.sectionOrder) {
            return 1;
        }
        return 0;
    }

    static tableReservationsFilter(table: Table | null, reservation: Reservation | null) {
        if (table == null || reservation == null) {
            return false;
        }

        const tableName: string = table?.tableName ?? '';
        if (Common.isNullOrEmpty(tableName)) {
            return false;
        }

        return !Common.isNullOrEmpty(reservation.reservationDetails?.allocatedTables)
            && reservation.reservationDetails.allocatedTables.split(',').includes(tableName)
    }

    static completedReservationsFilter(reservation: Reservation | null) {
        if (reservation?.reservationDetails == null) {
            return false;
        }

        const status = reservation.reservationDetails.reservationsStatus;
        return !(status === 'CheckedOut' || status === 'Cancelled' || status === 'NotShown' || status === 'Rejected');
    }

    static getResrvationAllocatedTableNames(reservation: Reservation | null) {
        if (reservation?.reservationDetails?.allocatedTables == null) {
            return [];
        }

        return reservation?.reservationDetails?.allocatedTables
            .split(',')
            .filter(t => !Common.isNullOrEmpty(t));
    }

    static padStart(num: number | string, maxLength: number) {
        return `${num}`.padStart(maxLength, '0');
    }

    static onlyUnique(value: any, index: number, array: any[]) {
        return array.indexOf(value) === index;
    }

    static minutesToHoursAndMinutesFormat(minutes: number | null | undefined): { hours: number, minutes: number } {
        const time = { hours: 0, minutes: 0 };

        if (minutes == null || minutes <= 0) {
            return time;
        }

        time.hours = Math.floor(minutes / 60);
        time.minutes = minutes - (time.hours * 60);

        return time;
    }

    static hoursAndMinutesToMinutesFormat(hours: number, minutes: number): number {
        return hours * 60 + minutes;
    }

    static removeCountryCodeFromPhoneNumber(phoneNumber: string) {

        for (let countryCode of Common.countryCodeList) {

            const alternativeCountryCode = countryCode.code.replace('+', '00');
            
            if (phoneNumber.startsWith(countryCode.code)) {
                return phoneNumber.substring(countryCode.code.length);
            } else if (phoneNumber.startsWith(alternativeCountryCode)) {
                return phoneNumber.substring(alternativeCountryCode.length);
            }
        }

        return phoneNumber;
    }

}